import React, { useState, useEffect } from 'react';
import { Mail, Phone, User, Calendar, MessageSquare, Check, X, Trash2 } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useToast } from '../../hooks/use-toast';

const ContactsManagement = () => {
  const { toast } = useToast();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchContacts();
  }, [filterStatus]);

  const fetchContacts = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = filterStatus === 'all' 
        ? `${BACKEND_URL}/api/contacts/`
        : `${BACKEND_URL}/api/contacts/?status=${filterStatus}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setContacts(data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast({
        title: 'خطأ',
        description: 'فشل في تحميل الرسائل',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (contactId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/contacts/${contactId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast({
          title: 'تم التحديث',
          description: 'تم تحديث حالة الرسالة بنجاح'
        });
        fetchContacts();
      }
    } catch (error) {
      console.error('Error updating contact:', error);
      toast({
        title: 'خطأ',
        description: 'فشل في تحديث الرسالة',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (contactId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه الرسالة؟')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/contacts/${contactId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast({
          title: 'تم الحذف',
          description: 'تم حذف الرسالة بنجاح'
        });
        fetchContacts();
        setSelectedContact(null);
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast({
        title: 'خطأ',
        description: 'فشل في حذف الرسالة',
        variant: 'destructive'
      });
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      unread: { text: 'غير مقروءة', class: 'bg-yellow-100 text-yellow-800' },
      read: { text: 'مقروءة', class: 'bg-blue-100 text-blue-800' },
      replied: { text: 'تم الرد', class: 'bg-green-100 text-green-800' }
    };
    const badge = badges[status] || badges.unread;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${badge.class}`}>
        {badge.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0F388E]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#0F388E]">إدارة رسائل التواصل</h1>
            <p className="text-gray-600 mt-2">عرض وإدارة رسائل المستخدمين</p>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="mb-6 flex gap-3">
          <Button
            variant={filterStatus === 'all' ? 'default' : 'outline'}
            onClick={() => setFilterStatus('all')}
            className={filterStatus === 'all' ? 'bg-[#0F388E]' : ''}
          >
            الكل ({contacts.length})
          </Button>
          <Button
            variant={filterStatus === 'unread' ? 'default' : 'outline'}
            onClick={() => setFilterStatus('unread')}
            className={filterStatus === 'unread' ? 'bg-yellow-600' : ''}
          >
            غير مقروءة
          </Button>
          <Button
            variant={filterStatus === 'read' ? 'default' : 'outline'}
            onClick={() => setFilterStatus('read')}
            className={filterStatus === 'read' ? 'bg-blue-600' : ''}
          >
            مقروءة
          </Button>
          <Button
            variant={filterStatus === 'replied' ? 'default' : 'outline'}
            onClick={() => setFilterStatus('replied')}
            className={filterStatus === 'replied' ? 'bg-green-600' : ''}
          >
            تم الرد
          </Button>
        </div>

        {contacts.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">لا توجد رسائل حالياً</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Messages List */}
            <div className="space-y-4">
              {contacts.map((contact) => (
                <Card
                  key={contact.id}
                  className={`cursor-pointer transition-all hover:shadow-lg ${
                    selectedContact?.id === contact.id ? 'ring-2 ring-[#0F388E]' : ''
                  } ${contact.status === 'unread' ? 'bg-yellow-50' : ''}`}
                  onClick={() => setSelectedContact(contact)}
                >
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#0F388E] rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-800">{contact.name}</h3>
                          <p className="text-sm text-gray-600">{contact.email}</p>
                        </div>
                      </div>
                      {getStatusBadge(contact.status)}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(contact.created_at).toLocaleDateString('ar-EG')}</span>
                    </div>
                    <h4 className="font-semibold text-gray-800 mb-2">{contact.subject}</h4>
                    <p className="text-gray-600 text-sm line-clamp-2">{contact.message}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Message Detail */}
            <div className="lg:sticky lg:top-6 h-fit">
              {selectedContact ? (
                <Card>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-6">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-[#0F388E] rounded-full flex items-center justify-center">
                          <User className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h2 className="text-xl font-bold text-gray-800">{selectedContact.name}</h2>
                          {getStatusBadge(selectedContact.status)}
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(selectedContact.id)}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    <div className="space-y-4 mb-6">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Mail className="w-5 h-5" />
                        <span>{selectedContact.email}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Phone className="w-5 h-5" />
                        <span dir="ltr">{selectedContact.phone}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Calendar className="w-5 h-5" />
                        <span>{new Date(selectedContact.created_at).toLocaleString('ar-EG')}</span>
                      </div>
                    </div>

                    <div className="border-t pt-4 mb-6">
                      <h3 className="font-bold text-gray-800 mb-2">الموضوع:</h3>
                      <p className="text-gray-700 mb-4">{selectedContact.subject}</p>
                      
                      <h3 className="font-bold text-gray-800 mb-2">الرسالة:</h3>
                      <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{selectedContact.message}</p>
                    </div>

                    <div className="flex gap-3">
                      {selectedContact.status === 'unread' && (
                        <Button
                          onClick={() => handleStatusChange(selectedContact.id, 'read')}
                          className="flex-1 bg-blue-600 hover:bg-blue-700"
                        >
                          <Check className="w-4 h-4 ml-2" />
                          وضع علامة كمقروءة
                        </Button>
                      )}
                      {selectedContact.status !== 'replied' && (
                        <Button
                          onClick={() => handleStatusChange(selectedContact.id, 'replied')}
                          className="flex-1 bg-green-600 hover:bg-green-700"
                        >
                          <MessageSquare className="w-4 h-4 ml-2" />
                          تم الرد
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-12 text-center">
                    <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">اختر رسالة لعرض التفاصيل</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactsManagement;
